import { useMemo } from 'react';
import { useCampfireQuery } from '../../../../../../global/network/useCampfireQuery';
import { GET_MATCHING_RULES } from './matching-rules-model.gql';
import { GetMatchingRules } from './__generated__/GetMatchingRules';

export interface MatchingRuleSession {
  matchingRuleSessionId: string;
  name: string;
  isFlexible: boolean;
  position: number;
  reportType: { reportTypeId: string; name: string } | null;
}

export interface MatchingRule {
  matchingRuleId: string;
  name: string;
  enabled: boolean;
  ruleOrder: number;
  matchOnAvailability: boolean;
  bidirectional: boolean;
  minimumScore: number;
  action: string;
  actionScheduling: string;
  actionFrequency: string;
  actionDurationMonths: number;
  applicantRole: { activityRoleId: string; name: string };
  counterpartRole: { activityRoleId: string; name: string };
  program: { programId: string; name: string } | null;
  sessions: MatchingRuleSession[];
}

interface ApplicationRoleLike {
  role: { activityRoleId: string; name: string };
}

export const useMatchingRules = () => {
  const { data, loading } = useCampfireQuery<GetMatchingRules, {}>(GET_MATCHING_RULES);

  const enabled = data?.vm.configItem?.configItemValue?.flagValue ?? data?.vm.configItem?.defaultValue ?? false;

  const rules = useMemo(() => {
    if (!enabled) {
      return [] as MatchingRule[];
    }

    return [...((data?.vm.matchingRules ?? []) as MatchingRule[])]
      .filter((rule) => rule.enabled)
      .sort((a, b) => a.ruleOrder - b.ruleOrder);
  }, [data, enabled]);

  /**
   * The first rule covering a role the person holds decides which counterparts we
   * suggest. A bidirectional rule also covers its counterpart role, suggesting
   * people in the applicant role. Rules are ordered, so an admin controls precedence.
   */
  const findRuleForRoles = (applicationRoles: ApplicationRoleLike[] | null | undefined) => {
    if (!applicationRoles) {
      return null;
    }

    const holds = (roleId: string) => applicationRoles.find((ar) => ar.role.activityRoleId === roleId);

    const rule = rules.find(
      (candidate) =>
        holds(candidate.applicantRole.activityRoleId) ||
        (candidate.bidirectional && holds(candidate.counterpartRole.activityRoleId))
    );

    if (!rule) {
      return null;
    }

    const held = holds(rule.applicantRole.activityRoleId);
    if (held) {
      return { rule, matchedRole: held.role, suggestedRole: rule.counterpartRole };
    }

    const reverseHeld = holds(rule.counterpartRole.activityRoleId);

    return reverseHeld ? { rule, matchedRole: reverseHeld.role, suggestedRole: rule.applicantRole } : null;
  };

  return { enabled, rules, findRuleForRoles, loading };
};
